import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/articles" className="text-primary-600 text-sm hover:underline cursor-pointer">返回文章列表</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': article.title,
    'description': article.excerpt,
    'author': {
      '@type': 'Person',
      'name': article.author,
    },
    'datePublished': article.publishDate,
    'image': article.coverImage,
    'publisher': {
      '@type': 'Organization',
      'name': '性福寶',
      'url': 'https://xingfubao.com/',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} | 性福寶健康資訊`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://xingfubao.com/articles/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首頁', href: '/' },
              { label: '健康資訊', href: '/articles' },
              { label: article.title },
            ]}
          />
        </div>

        <article className="py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs bg-secondary-100 text-secondary-700 px-2.5 py-0.5 rounded-full whitespace-nowrap">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.publishDate}</span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-background-100 text-foreground-600 px-2.5 py-1 rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="w-full h-48 md:h-72 rounded-lg overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 性福寶`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-sm max-w-none text-foreground-800 leading-relaxed
                [&_h2]:font-heading [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-4
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_ul]:mb-4 [&_ul]:pl-5 [&_li]:mb-2 [&_li]:leading-relaxed
                [&_strong]:font-semibold [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="border-t border-background-200/70 mt-12 pt-8">
              <Link
                to="/articles"
                className="inline-flex items-center gap-1.5 text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回文章列表
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}

export default ArticleDetailPage;